import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Send, RefreshCw, Inbox, User, Clock, AlertTriangle } from "lucide-react";
import SurchargeBlastButton from "./SurchargeBlastButton";
import getApiUrl from "@/utils/api";
import { fmtChicagoMonthDay, fmtChicagoDateTime } from "@/utils/timezone";
import { formatConfirmation } from "@/utils/confirmation";

const API = getApiUrl();

export function MessagesTab({ getAuthHeaders }) {
  const [conversations, setConversations] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeConv, setActiveConv] = useState(null);
  const [replyText, setReplyText] = useState("");

  useEffect(() => { fetchInbox(); }, []);

  const fetchInbox = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/messages/inbox`, getAuthHeaders());
      setConversations(res.data.conversations || []);
      setUnreadTotal(res.data.unread_total || 0);
    } catch (e) { toast.error("Failed to load messages"); }
    setLoading(false);
  };

  const markRead = async (bookingId) => {
    try {
      await axios.put(`${API}/admin/messages/${bookingId}/mark-read`, {}, getAuthHeaders());
      fetchInbox();
    } catch (e) {}
  };

  const sendReply = async () => {
    if (!replyText.trim() || !activeConv?.booking_id) return;
    try {
      const res = await axios.post(`${API}/admin/bookings/${activeConv.booking_id}/send-message`, { message: replyText }, getAuthHeaders());
      const status = res.data?.twilio_status || "queued";
      toast.success(`Message ${status} for ${activeConv.phone || "recipient"}`);
      setReplyText("");
      const conv = await axios.get(`${API}/admin/bookings/${activeConv.booking_id}/messages`, getAuthHeaders());
      setActiveConv(prev => ({ ...prev, messages: conv.data }));
      fetchInbox();
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to send message";
      toast.error(detail, { duration: 8000 });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="w-8 h-8 mx-auto animate-spin text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="messages-tab">
      {/* Twilio Sandbox Warning */}
      <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 md:p-4" data-testid="sandbox-warning">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <p className="font-bold text-amber-900 mb-1">WhatsApp Sandbox Limitation</p>
            <p className="text-amber-800 text-xs md:text-sm">
              Messages you send here only deliver to customers who have <span className="font-bold">first texted <code className="bg-amber-100 px-1 rounded">join on-take</code></span> to <a href="https://wa.me/14155238886" target="_blank" rel="noopener noreferrer" className="underline font-semibold">+1 (415) 523-8886</a>.
              If a customer says they didn't get your message, ask them to opt-in first. (Twilio sandbox is limited to 50 msgs/day; upgrade to a live WhatsApp Business sender to remove this limit.)
            </p>
          </div>
          <div className="flex-shrink-0">
            <SurchargeBlastButton getAuthHeaders={getAuthHeaders} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Conversation List */}
      <Card className="border-0 shadow-lg rounded-2xl md:col-span-1">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Inbox className="w-4 h-4" />
              Inbox
              {unreadTotal > 0 && (
                <Badge className="bg-red-500 text-white text-xs">{unreadTotal}</Badge>
              )}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchInbox} data-testid="refresh-inbox-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-2 max-h-[600px] overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">No conversations yet</p>
              <p className="text-xs text-muted-foreground mt-1">Messages will appear here when you message passengers</p>
            </div>
          ) : (
            <div className="space-y-1">
              {conversations.map((conv, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setActiveConv(conv);
                    if (conv.booking_id && conv.unread > 0) markRead(conv.booking_id);
                  }}
                  className={`w-full text-left p-3 rounded-xl transition-colors ${
                    activeConv?.booking_id === conv.booking_id
                      ? 'bg-green-50 border border-green-200'
                      : 'hover:bg-gray-50'
                  }`}
                  data-testid={`conv-${conv.booking_id || i}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-green-700" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{conv.passenger_name}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{conv.phone}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {conv.unread > 0 && (
                        <Badge className="bg-red-500 text-white text-[10px] px-1.5">{conv.unread}</Badge>
                      )}
                      <span className="text-[9px] text-muted-foreground">
                        {conv.last_message_at ? fmtChicagoMonthDay(conv.last_message_at) : ''}
                      </span>
                    </div>
                  </div>
                  {conv.messages?.[0] && (
                    <p className="text-xs text-muted-foreground mt-1 truncate pl-10">
                      {conv.messages[0].direction === "incoming" ? "" : "You: "}
                      {conv.messages[0].message}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Conversation */}
      <Card className="border-0 shadow-lg rounded-2xl md:col-span-2">
        <CardContent className="p-0">
          {!activeConv ? (
            <div className="text-center py-16">
              <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground/20 mb-4" />
              <p className="text-muted-foreground">Select a conversation to view messages</p>
            </div>
          ) : (
            <div className="flex flex-col h-[600px]">
              {/* Header */}
              <div className="px-4 py-3 border-b flex items-center gap-3 bg-gray-50 rounded-t-2xl">
                <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-sm">{activeConv.passenger_name}</p>
                  <p className="text-xs text-muted-foreground">{activeConv.phone}</p>
                </div>
                {activeConv.booking_id && (
                  <Badge variant="outline" className="ml-auto text-[10px]">{formatConfirmation(activeConv.booking_id)}</Badge>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="conversation-messages">
                {[...(activeConv.messages || [])].reverse().map((msg, i) => (
                  <div key={i} className={`flex ${msg.direction === "incoming" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-2xl ${
                      msg.direction === "incoming"
                        ? "bg-white border border-gray-200 rounded-tl-sm"
                        : "bg-green-600 text-white rounded-tr-sm"
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-[9px] mt-1 ${msg.direction === "incoming" ? "text-muted-foreground" : "text-green-100"}`}>
                        {msg.sent_at ? fmtChicagoDateTime(msg.sent_at) : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              <div className="px-4 py-3 border-t bg-gray-50 rounded-b-2xl">
                <div className="flex gap-2">
                  <Input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 h-10"
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendReply()}
                    data-testid="inbox-reply-input"
                  />
                  <Button
                    className="h-10 bg-green-600 hover:bg-green-700 px-4"
                    onClick={sendReply}
                    disabled={!replyText.trim()}
                    data-testid="inbox-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
